import { useEffect } from "react";
import { tamBaslik, kisaAciklama } from "@/lib/seo_metin";
import { useLocation } from "wouter";

interface SEOConfig {
  title: string;
  description: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  noindex?: boolean;
}

const pageSEO: Record<string, SEOConfig> = {
  "/": {
    title: "AdsManager | Ajans Reklam Hesabı Kiralama ve Reklam Yönetimi",
    description: "AdsManager: Meta, Google ve TikTok için yüksek limitli ajans reklam hesabı kiralama, reklam yönetimi ve danışmanlık. Kurulum ücreti yok, aynı gün aktivasyon.",
    canonical: "https://adsmanager.com.tr/",
  },
  "/ozellikler": {
    title: "Özellikler - AdsManager | Reklam Yönetimi İçin Güçlü Araçlar",
    description: "AdsManager'ın güçlü özelliklerini keşfedin: Otomatik raporlama, ROAS takibi, çoklu hesap yönetimi, hedef kitle analizi, bütçe optimizasyonu, A/B test yönetimi ve AI destekli öneriler.",
    canonical: "https://adsmanager.com.tr/ozellikler",
    ogTitle: "AdsManager Özellikleri - Reklam Yönetiminde Devrim",
    ogDescription: "8 güçlü özellik ile dijital reklamlarınızı profesyonelce yönetin. AI destekli optimizasyon, otomatik raporlama ve daha fazlası.",
  },
  "/fiyatlar": {
    title: "Fiyatlar | Reklam Hesabı Kiralama ve Reklam Danışmanlığı — AdsManager",
    description: "Facebook, Google Ads ve TikTok ajans reklam hesabı kiralama: %3'ten başlayan komisyon, kurulum ücreti ve minimum bütçe yok, aynı gün aktivasyon. Reklam yönetimi ve danışmanlık paketleri, ücretsiz ön denetim.",
    canonical: "https://adsmanager.com.tr/fiyatlar",
    ogTitle: "AdsManager Fiyatları: Hesap Kiralama ve Reklam Danışmanlığı",
    ogDescription: "Ajans reklam hesabında %3'ten başlayan komisyon, kurulum ücreti ve minimum bütçe yok. Reklam yönetimi paketleri ve ücretsiz ön denetim.",
  },
  "/giris": {
    title: "Giriş Yap - AdsManager",
    description: "AdsManager hesabınıza giriş yapın ve reklam kampanyalarınızı yönetmeye başlayın.",
    noindex: true,
  },
};

export function useSEO(overrides?: Partial<SEOConfig>) {
  const [location] = useLocation();

  useEffect(() => {
    const config = { ...pageSEO[location], ...overrides };
    if (!config.title) return;

    // Title
    document.title = tamBaslik(config.title);
    const aciklama = config.description ? kisaAciklama(config.description) : "";

    // Meta description
    const descMeta = document.querySelector('meta[name="description"]');
    if (descMeta && config.description) {
      descMeta.setAttribute("content", aciklama);
    }

    // Canonical
    let canonicalEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (config.canonical) {
      if (!canonicalEl) {
        canonicalEl = document.createElement("link");
        canonicalEl.setAttribute("rel", "canonical");
        document.head.appendChild(canonicalEl);
      }
      canonicalEl.setAttribute("href", config.canonical);
    }

    // OG Title
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute("content", config.ogTitle || config.title);
    }

    // OG Description
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc && config.description) {
      ogDesc.setAttribute("content", kisaAciklama(config.ogDescription || config.description));
    }

    // OG URL
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl && config.canonical) {
      ogUrl.setAttribute("content", config.canonical);
    }

    // Twitter Title
    const twTitle = document.querySelector('meta[name="twitter:title"]');
    if (twTitle) {
      twTitle.setAttribute("content", config.ogTitle || config.title);
    }

    // Twitter Description
    const twDesc = document.querySelector('meta[name="twitter:description"]');
    if (twDesc && config.description) {
      twDesc.setAttribute("content", kisaAciklama(config.ogDescription || config.description));
    }

    // Robots noindex
    let robotsMeta = document.querySelector('meta[name="robots"]');
    if (config.noindex) {
      if (robotsMeta) {
        robotsMeta.setAttribute("content", "noindex, nofollow");
      }
    } else {
      if (robotsMeta) {
        robotsMeta.setAttribute("content", "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1");
      }
    }
  }, [location, overrides]);
}

export default useSEO;
